/*
 * i18next setup. Every `locales/<lang>/<namespace>.json` is discovered at build time with import.meta.glob,
 * so an area adds `locales/ar/<ns>.json` + `locales/en/<ns>.json` and calls `useTranslation('<ns>')`;
 * nothing else to register. Keys are typed from the Arabic files (resources.gen.d.ts, generated).
 * Arabic plurals use the six CLDR forms (_zero _one _two _few _many _other); English uses _one and _other.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { storage } from '@/lib/storage'
import { DEFAULT_LOCALE, LOCALE_STORAGE_KEY, dirOf, isLocale, type Locale } from './locale'

type Json = { [key: string]: unknown }
const modules = import.meta.glob<{ default: Json }>('./locales/*/*.json', { eager: true })

const resources: Record<string, Record<string, Json>> = {}
for (const [path, mod] of Object.entries(modules)) {
  const m = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/)
  if (!m) continue
  const [, lang, ns] = m as unknown as [string, string, string]
  resources[lang] ??= {}
  resources[lang][ns] = mod.default
}

export const namespaces = Object.keys(resources[DEFAULT_LOCALE] ?? {})

function initialLocale(): Locale {
  const saved = storage.get(LOCALE_STORAGE_KEY)
  return isLocale(saved) ? saved : DEFAULT_LOCALE
}

/** Applies lang and dir on <html>, so the whole document (and Radix portals) flips live. */
function applyDocumentLocale(locale: Locale) {
  const el = document.documentElement
  el.lang = locale
  el.dir = dirOf(locale)
}

const lng = initialLocale()
applyDocumentLocale(lng)

void i18n.use(initReactI18next).init({
  resources,
  lng,
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: ['ar', 'en'],
  ns: namespaces,
  defaultNS: 'common',
  interpolation: { escapeValue: false },
  returnNull: false,
  react: { useSuspense: false },
})

i18n.on('languageChanged', (l) => {
  if (isLocale(l)) applyDocumentLocale(l)
})

/** Switches the language live (direction included) and remembers it on this device. */
export function setLocale(locale: Locale) {
  storage.set(LOCALE_STORAGE_KEY, locale)
  void i18n.changeLanguage(locale)
}

export { i18n }
